import React from 'react';
import NotificationIcon, { NOTIFICATION_TONE_CLASSES } from './NotificationIcon';
import { getNotificationSummaryGroups } from '../../utils/notificationUtils';

const MAX_VISIBLE_GROUPS = 7;

const NotificationSummaryList = ({
  notifications = [],
  loading = false,
  totalCount = 0,
  primaryColor = '#3B5BDB',
  onOpenGroup,
  onViewAll,
}) => {
  const groups = getNotificationSummaryGroups(notifications);
  const visibleGroups = groups.slice(0, MAX_VISIBLE_GROUPS);
  const hiddenCount = Math.max(0, groups.length - visibleGroups.length);

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <div
          className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-transparent dark:border-gray-600"
          style={{ borderTopColor: primaryColor }}
        />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="px-4 py-10 text-center">
        <svg className="mx-auto mb-3 h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 1 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">No pending notifications</p>
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="px-2 py-2">
      <div className="px-2 pb-2 text-xs text-gray-500 dark:text-gray-400">
        {totalCount || notifications.length} pending notification{(totalCount || notifications.length) === 1 ? '' : 's'} across {groups.length} type{groups.length === 1 ? '' : 's'}.
      </div>

      <div className="space-y-1">
        {visibleGroups.map((group) => {
          const tone = NOTIFICATION_TONE_CLASSES[group.severity] || NOTIFICATION_TONE_CLASSES.default;
          return (
            <button
              key={group.type}
              type="button"
              onClick={() => onOpenGroup?.(group)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:hover:bg-gray-700/40 dark:focus:ring-blue-900/40"
            >
              <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${tone.iconWrap}`}>
                <NotificationIcon icon={group.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white">
                  {group.title}
                </span>
                <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {group.categoryLabel}
                </span>
              </span>
              <span
                className="flex h-6 min-w-6 items-center justify-center rounded-full px-2 text-xs font-bold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {group.count}
              </span>
            </button>
          );
        })}
      </div>

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onViewAll}
          className="mt-2 w-full rounded-lg px-3 py-2 text-left text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/40 dark:hover:text-gray-200"
        >
          + {hiddenCount} more notification type{hiddenCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
};

export default NotificationSummaryList;
